import React, { useCallback } from 'react';
import TooltipV2 from '@/components/TooltipV2';
import Icon from '@/components/atoms/Icon';

interface VideoControlsProps {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  onPlayPause: () => void;
  onSkipBackward: () => void;
  onSkipForward: () => void;
}

const VideoControls: React.FC<VideoControlsProps> = ({
  isPlaying,
  currentTime,
  duration,
  onPlayPause,
  onSkipBackward,
  onSkipForward
}) => {
  const formatTime = useCallback((time: number): string => {
    const clampedTime = Math.max(0, time);
    const minutes = Math.floor(clampedTime / 60);
    const seconds = Math.floor(clampedTime % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  return (
    <>
      {/* Left Controls */}
      <div className="relative flex items-center gap-3">
        <TooltipV2
          tooltipId="play-tooltip"
          tooltipElement={
            <button
              onClick={onPlayPause}
              className="flex cursor-pointer items-center justify-center text-white/75 hover:text-white"
              data-testid="play-pause-button"
            >
              <Icon name={isPlaying ? 'IconPlayerPause' : 'IconPlayerPlayFilled'} size={16} />
            </button>
          }
          tooltipContent={isPlaying ? 'Pause' : 'Play'}
          size="xsmall"
          colorTheme="dark"
          place="top"
          offset={25}
          className="z-50 !rounded-md bg-slate-950 px-3 py-2 !text-[10px] font-normal !leading-[15px] text-white"
        />

        <TooltipV2
          tooltipId="rewind-tooltip"
          tooltipElement={
            <button
              onClick={onSkipBackward}
              className="flex cursor-pointer items-center justify-center text-white/75 hover:text-white"
              data-testid="skip-backward-button"
            >
              <Icon name="IconRewindBackward5" size={16} />
            </button>
          }
          tooltipContent="Rewind 5s"
          size="xsmall"
          colorTheme="dark"
          place="top"
          offset={25}
          className="z-50 !rounded-md bg-slate-950 px-3 py-2 !text-[10px] font-normal !leading-[15px] text-white"
        />

        {/* Time Display */}
        <div className="cursor-default text-xs tabular-nums text-white" data-testid="time-display">
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>

        <TooltipV2
          tooltipId="forward-tooltip"
          tooltipElement={
            <button
              onClick={onSkipForward}
              className="flex cursor-pointer items-center justify-center text-white/75 hover:text-white"
              data-testid="skip-forward-button"
            >
              <Icon name="IconRewindForward5" size={16} />
            </button>
          }
          tooltipContent="Forward 5s"
          size="xsmall"
          colorTheme="dark"
          place="top"
          offset={25}
          className="z-50 !rounded-md bg-slate-950 px-3 py-2 !text-[10px] font-normal !leading-[15px] text-white"
        />
      </div>
    </>
  );
};

export default VideoControls;
